import { setAuthFromToken } from '@/features/auth/authSlice'
import apiClient, { setAuthToken } from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { parseVerifyUserResponse } from '@/lib/api/userService'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { clearImpersonationSessionFilters } from '@/lib/auth/impersonationSessionFilters'
import {
  getPostImpersonateReactPath,
  type PostImpersonateContext,
} from '@/lib/utils/postImpersonateRedirect'
import { getCurrentPathForImpersonateReturn } from '@/lib/utils/impersonateReturnPath'
import type { AppDispatch } from '@/app/store'

function parseImpersonateResponse(data: unknown): { token: string } {
  if (typeof data === 'string') {
    return { token: data }
  }
  if (data && typeof data === 'object' && 'token' in data) {
    return { token: (data as { token?: string }).token ?? '' }
  }
  return { token: '' }
}

/**
 * Staff/admin impersonation: matches Application list + Angular user list — POST impersonate,
 * persist admin backup tokens, verify session, update Redux, full navigation to post-impersonate route.
 */
export async function staffImpersonateAndRedirect(
  dispatch: AppDispatch,
  roleId: number | string,
  postImpersonateCtx?: PostImpersonateContext,
): Promise<void> {
  const currentToken = localStorage.getItem(STORAGE_KEYS.authToken)
  if (currentToken) {
    localStorage.setItem('admin_token', currentToken)
  }
  const returnPath = getCurrentPathForImpersonateReturn()
  localStorage.setItem('impersonate_path', returnPath)
  localStorage.setItem('impersonatepath', returnPath)
  sessionStorage.setItem('impersonate_process', '1')

  const roleIdNum = Number(roleId)
  const res = await apiClient.post(API_ENDPOINTS.IMPERSONATE, {
    role_id: Number.isFinite(roleIdNum) ? roleIdNum : roleId,
  })

  const { token } = parseImpersonateResponse(res.data)
  if (!token) {
    throw new Error('Impersonate response missing token.')
  }

  localStorage.setItem('old_token', currentToken || '')
  setAuthToken(token)

  const verifyRes = await apiClient.get<unknown>(`${API_ENDPOINTS.VERIFY_USER}?force`)
  const userData = parseVerifyUserResponse(verifyRes.data)
  dispatch(setAuthFromToken({ token, user: userData }))

  try {
    localStorage.setItem('user', JSON.stringify(userData))
  } catch {
    /* ignore */
  }
  localStorage.setItem('impersonate', 'true')
  clearImpersonationSessionFilters()

  const nextPath = getPostImpersonateReactPath(
    userData as unknown as Record<string, unknown>,
    postImpersonateCtx,
  )
  if (localStorage.getItem('impersonate') !== 'true') {
    sessionStorage.removeItem('impersonate_process')
  }
  if (nextPath === window.location.pathname) {
    window.location.reload()
    return
  }
  window.location.href = nextPath
}

export function rollbackStaffImpersonationAttempt(): void {
  if (localStorage.getItem('impersonate') !== 'true') {
    localStorage.removeItem('admin_token')
    sessionStorage.removeItem('impersonate_process')
  }
}
